import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfFont, PageGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/helpers';
import '../../pdf/draw/transforms';

/**
 * Zasunovacka (Slider) - Sliding puzzle cards with zigzag paths
 *
 * Layout: Multiple portrait pages, 2×5 grid per page.
 * Each card: left label, zigzag slot path, right label.
 *
 * Migrated from C# EduFrame.Img10.Generators.Zasunovacka
 */
export class Zasunovacka implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const itemsPerPage = 10;
    const pagesNeeded = Math.ceil(opts.list.length / itemsPerPage);
    const pages: PageGen[] = [];
    for (let i = 0; i < pagesNeeded; i++) {
      pages.push(pdf.addPage());
    }

    const margin = PdfUnit.FromMillimeters(5);
    const sizeW = (PdfSize.A4.width - 2 * margin) / 2;
    const sizeH = (PdfSize.A4.height - 2 * margin) / 5;
    const count = Math.max(3, findMinNForOptions(opts.list.length));

    const labelWidth = sizeW / 3;
    const labelSize = new PdfSize(labelWidth, sizeH);
    const diff = labelWidth / 4;

    const leftTexts = opts.list.map((item) => [item.left.getText() ?? '']);
    const rightTexts = opts.list.map((item) => [item.right.getText() ?? '']);

    const leftFont = pages[0].adjustFontMulti(leftTexts, GUtil.baseFont, labelSize);
    const rightFont = pages[0].adjustFontMulti(rightTexts, GUtil.baseFont, labelSize);

    const zigzagPatterns = generateZigzagPatterns(count, opts.list.length, opts.data.random);

    for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
      const page = pages[pageIndex];
      const pageStartIndex = pageIndex * itemsPerPage;

      for (let x = 0; x < 2; x++) {
        for (let y = 0; y < 5; y++) {
          const index = pageStartIndex + x * 5 + y;
          if (index >= opts.list.length) continue;

          const list = zigzagPatterns[index];
          const rect = new PdfRect(margin + x * sizeW, margin + y * sizeH, sizeW, sizeH);

          const rLeft = new PdfRect(rect.left, rect.top, labelWidth, sizeH);
          const rRight = new PdfRect(rect.right - labelWidth, rect.top, labelWidth, sizeH);

          page.drawLabel(opts.list[index].left, { rect: rLeft, font: leftFont });
          page.drawLabel(opts.list[index].right, { rect: rRight, font: rightFont });

          for (let i = 0; i < count; i++) {
            const zigzagX = rLeft.right + (1 + list[i]) * diff;
            const y0 = rect.top + i * (sizeH / count);
            const y1 = rect.top + (i + 1) * (sizeH / count);

            page.drawLine(GUtil.cutPen, new PdfPoint(zigzagX, y0), new PdfPoint(zigzagX, y1));

            if (i + 1 < count) {
              const nextZigzagX = rLeft.right + (1 + list[i + 1]) * diff;
              page.drawLine(GUtil.cutPen, new PdfPoint(zigzagX, y1), new PdfPoint(nextZigzagX, y1));
            }
          }

          page.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}

function findMinNForOptions(x: number): number {
  if (x <= 0) return 0;

  let n = 0;
  while (countOptions(n) < x) {
    n++;
  }

  return n;
}

function countOptions(n: number): number {
  return 3**n - 2**(n + 1) + 1;
}

function generateZigzagPatterns(
  count: number,
  itemCount: number,
  random: { next: (min: number, max: number) => number }
): number[][] {
  const array = new Array<number[]>();
  const dictionary = new Set<string>();
  while (array.length < itemCount) {
    const list = Array.from({ length: count }, () => random.next(0, 3));
    if (list.includes(0) && list.includes(2)) {
      const str = list.join('.');
      if (!dictionary.has(str)) {
        dictionary.add(str);
        array.push(list);
      }
    }
  }
  return array;
}
